import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DirectActionRecipientType } from 'angular-to-phaser';
import { catchError, map, Observable, retry, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import Constants from '../constants/consts';
import { Interaction, Npc, RefreshTarget, RefreshTargetKind, replaceTokenInContent, Zone } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { InventoryService } from './inventory.service';
import { PetService } from './pet.service';

@Injectable({
    providedIn: 'root'
})
export class WorldService extends AbstractService {

    constructor(private httpClient: HttpClient, private coreAppService: ApplicationService, private inventoryService: InventoryService, private petService: PetService) {
        super(coreAppService.authService, coreAppService.featureFlagService);
    }

    public getWorld(): Observable<Zone[]> {
        return this.httpClient.get<Zone[]>(environment.apiUrl + '/world/', this.httpOptionsAuthJson())
            .pipe(
                map(zones => zones.map(zone => new Zone(zone))),
                retry(2),
                catchError(this.handleError)
            );
    }

    public getZone(externalId: string): Observable<Zone> {
        return this.httpClient.get<Zone>(environment.apiUrl + `/world/zone/${externalId}`, this.httpOptionsAuthJson())
            .pipe(
                map(zone => new Zone(zone)),
                retry(2),
                catchError(this.handleError)
            );
    }

    public getNpc(npcId: string): Observable<Npc> {
        return this.httpClient.get<Npc>(environment.apiUrl + `/world/npc/${npcId}`, this.httpOptionsAuthJson())
            .pipe(
                map(npc => new Npc(npc)),
                retry(2),
                catchError(this.handleError),
            );
    }

    // continuationId is threaded ONLY by the refresh-on-close callers reached through
    // resolveRefreshTarget below (a re-greet right after closing a modal/minigame this NPC's own
    // dialogue opened) - a fresh walk-up greet (explore/click) must never supply one, or the
    // server has no way to distinguish "I was just here" from a genuinely fresh visit. See
    // Docs/CritterServer/Quest-Interaction-Architecture.md's continuation-signal section.
    public retrieveNpcOptions(npcId: string, continuationId?: number): Observable<Interaction> {
        const url = environment.apiUrl + `/world/npc/${npcId}/interact/hello`
            + (continuationId != null ? `?continuationId=${continuationId}` : '');
        return this.httpClient.post<Interaction>(url, null, this.httpOptionsAuthJson())
            .pipe(
                map(interaction => {
                    const hydrated = new Interaction(interaction, this.coreAppService.jsonMapper.jsonEnricher());
                    const petName = this.petService.activeUserPetsSignal()[0]?.Name;
                    if (petName) replaceTokenInContent(hydrated.Content, Constants.PET_NAME_REPLACE_TOKEN, petName);
                    return hydrated;
                }),
                retry(2),
                catchError(this.handleError),
            );
    }


    // continuationId - see retrieveNpcOptions's own comment.
    public retrieveZoneOptions(zoneId: string, continuationId?: number): Observable<Interaction> {
        const url = environment.apiUrl + `/world/zone/${zoneId}/interact/hello`
            + (continuationId != null ? `?continuationId=${continuationId}` : '');
        return this.httpClient.post<Interaction>(url, null, this.httpOptionsAuthJson())
            .pipe(
                map(interaction => {
                    const hydrated = new Interaction(interaction, this.coreAppService.jsonMapper.jsonEnricher());
                    const petName = this.petService.activeUserPetsSignal()[0]?.Name;
                    if (petName) replaceTokenInContent(hydrated.Content, Constants.PET_NAME_REPLACE_TOKEN, petName);
                    return hydrated;
                }),
                retry(2),
                catchError(this.handleError),
            );
    }

    // Shared resolution logic behind the automatic "regreet on close" default (see
    // Docs/CritterClient/Interaction-Content-Rendering.md's refresh-target section) - both
    // InteractorComponent.openNewModal and PhaserComponent.handlePhaserEvent call this so
    // there's exactly one place that decides which endpoint a RefreshTarget maps to. Null in
    // (no target resolved), null out - the caller does nothing rather than subscribing.
    // continuationId is the interaction id the caller was just looking at before this refresh -
    // see retrieveNpcOptions's own comment for why only this path ever supplies one.
    public resolveRefreshTarget(target: RefreshTarget, continuationId?: number): Observable<Interaction> | null {
        if (!target) return null;
        return target.Kind === RefreshTargetKind.Zone
            ? this.retrieveZoneOptions(target.ExternalId, continuationId)
            : this.retrieveNpcOptions(target.ExternalId, continuationId);
    }

    public submitAction(externalId: string, action: number, recipientType: DirectActionRecipientType, postBody: any = null): Observable<Interaction> {
        let recipientTypeUrl;
        let actionSegment = 'interact';
        switch (recipientType) {
            case DirectActionRecipientType.Npc:
                recipientTypeUrl = 'npc';
                break;
            case DirectActionRecipientType.Zone:
                recipientTypeUrl = 'zone';
                break;
            case DirectActionRecipientType.Quest:
                recipientTypeUrl = 'quest';
                break;
            case DirectActionRecipientType.QuestReply:
                recipientTypeUrl = 'quest'; // same path prefix as Quest — only the action segment differs
                actionSegment = 'reply';
                break;
            default: throw `WorldService is missing recipient type URL conversion for ${recipientType} - ${DirectActionRecipientType[recipientType]}`
        }

        return this.httpClient.post<Interaction>(environment.apiUrl + `/world/${recipientTypeUrl}/${externalId}/${actionSegment}/${action}`, postBody, this.httpOptionsAuthJson())
            .pipe(
                map(interaction => {
                    const hydrated = new Interaction(interaction, this.coreAppService.jsonMapper.jsonEnricher());
                    const petName = this.petService.activeUserPetsSignal()[0]?.Name;
                    if (petName) replaceTokenInContent(hydrated.Content, Constants.PET_NAME_REPLACE_TOKEN, petName);
                    return hydrated;
                }),
                tap(interaction => Interaction.distributeGameStateToServices(interaction, this.inventoryService, this.coreAppService.userService, this.petService)),
                retry(2),
                catchError(this.handleError),
            );
    }



}
